import { PublicIdSchemas } from '@idc-forms/id'
import { z } from 'zod'
import { TYPE_ACCOUNT } from '../entities/user/user'
import { email as emailPrimitive, requiredString } from '../primitives/string'
import { InstantSchema } from '../primitives/time'
import { MESSAGES } from '../utils/messages'
import { V2CursorPaginationSchema } from './http'

/**
 * Contrato de la administración v2 de usuarios de plataforma (staff/asesores).
 * Es el contrato OpenAPI que consume el SDK generado (@idc-forms/api-client) y
 * el panel web: cambiarlo es cambiar el contrato.
 *
 * SEGURIDAD: acá NUNCA viven credenciales ni secretos (password, refreshToken,
 * twoFactorAuthenticationSecret, config). El password inicial lo genera el
 * backend y viaja por correo; los cambios de credenciales van por self-service.
 *
 * Nomenclatura de producto (no de Mongo): `fullName`, `accountType`, `mfaEnabled`
 * en lugar de `fullname`/`typeAccount`/`isTwoFactorAuthenticationEnabled`.
 */

/** Nombre de usuario legible/único: identificador amigable en URLs y filtros. */
const usernameSchema = z
  .string({ required_error: MESSAGES.REQUIRED })
  .min(1, MESSAGES.REQUIRED)
  .regex(/^[a-zA-Z0-9]+$/, 'Solo letras y números, sin espacios')
  .toLowerCase()

/** Referencia a un rol dentro de un usuario: siempre por id público (`rol_`). */
export const V2AdminUserRoleRefSchema = z.object({
  id: PublicIdSchemas.role,
  name: z.string().min(1)
})

/** Fila del listado y payload del detalle. */
export const V2AdminUserSchema = z.object({
  id: PublicIdSchemas.user,
  fullName: z.string().min(1),
  username: z.string().min(1),
  email: z.string().email(),
  accountType: z.nativeEnum(TYPE_ACCOUNT),
  roles: z.array(V2AdminUserRoleRefSchema),
  active: z.boolean(),
  mfaEnabled: z.boolean(),
  mfaRequired: z.boolean(),
  avatar: z.string().nullable(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema
})

/** Página cursor-paginada del listado (ADR 0019). */
export const V2AdminUsersPageSchema = z.object({
  data: z.array(V2AdminUserSchema),
  pagination: V2CursorPaginationSchema
})

/** Total y facetas por tipo de cuenta, con los mismos filtros del listado. */
export const V2AdminUsersSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  accountTypes: z.object({
    admin: z.number().int().nonnegative(),
    user: z.number().int().nonnegative(),
    advisor: z.number().int().nonnegative()
  })
})

/**
 * Alta de usuario. El payload NO carga credenciales: el backend genera un
 * password aleatorio y lo envía por correo. Los roles se referencian por
 * id público (`rol_`); el backend los resuelve al persistir.
 */
export const V2CreateAdminUserSchema = z.object({
  fullName: requiredString(),
  email: emailPrimitive().toLowerCase(),
  username: usernameSchema,
  accountType: z.nativeEnum(TYPE_ACCOUNT),
  roleIds: z.array(PublicIdSchemas.role).default([]),
  avatar: z.string().url('URL inválida').optional(),
  active: z.boolean().optional()
})

/** Edición parcial. Credenciales y 2FA NO se aceptan por este contrato. */
export const V2UpdateAdminUserSchema = V2CreateAdminUserSchema.partial()

/** 202 de POST /admin/users/:id/password-reset — el correo se envía async. */
export const V2AdminPasswordResetAcceptedSchema = z.object({
  accepted: z.literal(true)
})

/**
 * Entrada mínima del selector de usuarios (`usr_` + nombre) para los formularios
 * que asignan personas —los autorizadores de un proyecto, y más adelante otros
 * consumidores—. Sólo usuarios activos; nada de credenciales ni secretos.
 */
export const V2AdminUserLookupItemSchema = z.object({
  id: PublicIdSchemas.user,
  fullName: z.string().min(1)
})

export const V2AdminUserLookupSchema = z.object({
  data: z.array(V2AdminUserLookupItemSchema)
})

export type V2AdminUserRoleRef = z.infer<typeof V2AdminUserRoleRefSchema>
export type V2AdminUser = z.infer<typeof V2AdminUserSchema>
export type V2AdminUsersPage = z.infer<typeof V2AdminUsersPageSchema>
export type V2AdminUsersSummary = z.infer<typeof V2AdminUsersSummarySchema>
export type V2CreateAdminUserInput = z.infer<typeof V2CreateAdminUserSchema>
export type V2UpdateAdminUserInput = z.infer<typeof V2UpdateAdminUserSchema>
export type V2AdminUserLookupItem = z.infer<typeof V2AdminUserLookupItemSchema>
export type V2AdminUserLookup = z.infer<typeof V2AdminUserLookupSchema>
